import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { apiFetch } from '../api/api';
import MessageModal from '../components/MessageModal';
import { APP_SERVICE_ID, isAppServiceItem, withServiceQuery } from '../config/service';
import {
  clearCart,
  removeFromCart,
  selectCartDiscount,
  selectCartItems,
  selectCartSubtotal,
  setItemQuantity,
} from '../store/cartSlice';
import { calculateDeliveryTotals, extractList, resolveCityFromCoords } from '../utils/deliveryZones';
import { getCachedLocation, resolveStoredOrCurrentLocation } from '../utils/location';
import { formatPrice, formatProductPrice, lineTotal } from '../utils/price';
import { isUserLoggedIn, readJsonSafe, getStoredUser } from '../utils/authSession';
import { buildCartWhatsAppMessage, openWhatsApp } from '../utils/whatsapp';

const pagePaperSx = {
  elevation: 0,
  variant: 'outlined',
  sx: {
    borderRadius: 4,
    bgcolor: 'background.paper',
    boxShadow: '0 4px 24px rgba(42,147,143,0.06)',
    borderColor: 'divider',
  },
};

const primaryButtonSx = {
  py: 1.5,
  borderRadius: 2.5,
  fontSize: '1rem',
  fontWeight: 800,
  background: 'linear-gradient(90deg, #3ab5b0, #2a938f)',
  boxShadow: '0 6px 18px rgba(42,147,143,0.28)',
  '&:hover': {
    background: 'linear-gradient(90deg, #2a938f, #1f6e6b)',
  },
};

const quantityStepperSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  p: 0.5,
  borderRadius: 2.5,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const quantityButtonSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  width: 36,
  height: 36,
};

function PageSectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ mb: { xs: 0.5, md: 1 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.5 } }}>
        <Box
          sx={{
            width: 5,
            height: { xs: 28, md: 34 },
            borderRadius: 999,
            flexShrink: 0,
            background: 'linear-gradient(180deg, #3ab5b0, #2a938f)',
            boxShadow: '0 2px 10px rgba(42,147,143,0.28)',
          }}
        />
        <Box>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              color: 'primary.main',
              fontSize: { xs: '1.45rem', md: '1.75rem' },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '0.95rem' }}>
              {subtitle}
            </Typography>
          ) : null}
          <Box
            sx={{
              mt: 0.75,
              width: { xs: 40, md: 52 },
              height: 3,
              borderRadius: 999,
              background: 'linear-gradient(90deg, #3ab5b0, #2a938f)',
              opacity: 0.85,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function OrderSummaryLine({ label, value, emphasize, success }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.85 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={emphasize ? 800 : 700}
        color={success ? 'success.main' : emphasize ? 'primary.main' : 'text.primary'}
      >
        {value}
      </Typography>
    </Box>
  );
}

function QuantityStepper({ quantity, onDecrease, onIncrease }) {
  return (
    <Box sx={quantityStepperSx}>
      <IconButton size="small" onClick={onDecrease} sx={quantityButtonSx}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography fontWeight={800} sx={{ minWidth: 32, textAlign: 'center', px: 0.25 }}>
        {quantity}
      </Typography>
      <IconButton
        size="small"
        onClick={onIncrease}
        sx={{
          ...quantityButtonSx,
          bgcolor: alpha('#2a938f', 0.08),
          '&:hover': { bgcolor: alpha('#2a938f', 0.14) },
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function LocationZoneSection({
  t,
  loading,
  ready,
  selectedCity,
  hasCoords,
  bestOfferTitle,
  onRefreshLocation,
  refreshingLocation,
}) {
  if (loading || !ready) {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          textAlign: 'center',
          bgcolor: alpha('#2a938f', 0.06),
          borderColor: alpha('#2a938f', 0.14),
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            mx: 'auto',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: '0 4px 12px rgba(42,147,143,0.12)',
          }}
        >
          <MapOutlinedIcon color="primary" />
        </Box>
        <CircularProgress size={22} sx={{ mb: 1.5 }} />
        <Typography fontWeight={800} color="primary.main" gutterBottom>
          {t('cart_zone_loading_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('cart_zone_loading_message')}
        </Typography>
      </Paper>
    );
  }

  const inZone = Boolean(selectedCity?.id);
  const cityLabel = selectedCity?.name
    ?? (hasCoords ? t('cart_city_outside') : t('cart_city_unknown'));

  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: inZone ? alpha('#2a938f', 0.07) : alpha('#d32f2f', 0.06),
          borderColor: inZone ? alpha('#2a938f', 0.16) : alpha('#d32f2f', 0.18),
        }}
      >
        {inZone ? (
          <LocationOnOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        ) : (
          <LocationOffOutlinedIcon sx={{ color: 'error.main', fontSize: 28 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
            {t('cart_delivery_city')}
          </Typography>
          <Typography variant="body1" fontWeight={800} color={inZone ? 'primary.main' : 'error.main'}>
            {cityLabel}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onRefreshLocation}
          disabled={refreshingLocation}
          aria-label={t('cart_refresh_location')}
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(42,147,143,0.1)',
          }}
        >
          {refreshingLocation ? <CircularProgress size={18} /> : <MyLocationOutlinedIcon fontSize="small" />}
        </IconButton>
      </Paper>

      {bestOfferTitle ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 1.75,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            bgcolor: alpha('#2e7d32', 0.07),
            borderColor: alpha('#2e7d32', 0.16),
          }}
        >
          <CardGiftcardOutlinedIcon sx={{ color: 'success.dark', mt: 0.25 }} />
          <Typography variant="body2" fontWeight={700} color="success.dark" lineHeight={1.6}>
            {bestOfferTitle}
          </Typography>
        </Paper>
      ) : null}

      <Button
        component={RouterLink}
        to="/delivery"
        variant="outlined"
        size="small"
        startIcon={<LocalShippingOutlinedIcon />}
        sx={{
          alignSelf: 'flex-start',
          borderRadius: 2.5,
          fontWeight: 700,
          textTransform: 'none',
          borderColor: alpha('#2a938f', 0.35),
          color: 'primary.main',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: alpha('#2a938f', 0.06),
          },
        }}
      >
        {t('cart_view_delivery_offers')}
      </Button>
    </Stack>
  );
}

function CartItemMobile({ entry, currency, onDecrease, onIncrease, onRemove }) {
  const itemTotal = lineTotal(entry.product.price, entry.quantity);
  return (
    <Paper
      {...pagePaperSx}
      sx={{
        ...pagePaperSx.sx,
        p: 2,
        transition: 'all 0.22s ease',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: '0 8px 24px rgba(42,147,143,0.12)',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          sx={{
            width: 92,
            height: 92,
            flexShrink: 0,
            borderRadius: 3,
            p: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: alpha('#2a938f', 0.05),
            background:
              'linear-gradient(180deg, rgba(58,181,176,0.1) 0%, rgba(42,147,143,0.04) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={entry.product.image}
            alt={entry.product.name}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} gutterBottom noWrap sx={{ fontSize: '0.98rem' }}>
            {entry.product.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {formatProductPrice(entry.product)} {currency}
          </Typography>
          {entry.note && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {entry.note}
            </Typography>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.75 }}>
            <QuantityStepper quantity={entry.quantity} onDecrease={onDecrease} onIncrease={onIncrease} />
            <Typography fontWeight={800} color="primary.main" sx={{ fontSize: '1.05rem' }}>
              {formatPrice(itemTotal)} {currency}
            </Typography>
          </Stack>
        </Box>
        <IconButton
          color="error"
          size="small"
          onClick={onRemove}
          sx={{
            alignSelf: 'flex-start',
            border: 1,
            borderColor: alpha('#d32f2f', 0.2),
            bgcolor: alpha('#d32f2f', 0.04),
          }}
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

export default function Cart() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const muiTheme = useMuiTheme();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'));
  const items = useSelector(selectCartItems);
  const subtotal = useSelector(selectCartSubtotal);
  const discountTotal = useSelector(selectCartDiscount);
  const currency = t('orders_currency');

  const [submitting, setSubmitting] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingReady, setPricingReady] = useState(false);
  const [cities, setCities] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [locationCoords, setLocationCoords] = useState(null);
  const [refreshingLocation, setRefreshingLocation] = useState(false);
  const [messageModal, setMessageModal] = useState({ visible: false, title: '', message: '', variant: 'info' });

  const hasItems = items.length > 0;
  const itemCount = items.reduce((sum, e) => sum + e.quantity, 0);

  const totals = useMemo(
    () => calculateDeliveryTotals({ subtotal, city: selectedCity, offers }),
    [subtotal, selectedCity, offers],
  );

  const showMessage = useCallback((title, message, variant = 'error') => {
    setMessageModal({ visible: true, title, message, variant });
  }, []);

  const loadPricingContext = useCallback(async ({ refreshLocation = false } = {}) => {
    if (!hasItems) return;
    if (refreshLocation) setRefreshingLocation(true);
    else setPricingLoading(true);

    try {
      const cachedCoords = refreshLocation ? null : getCachedLocation();
      const [citiesRes, offersRes, coords] = await Promise.all([
        cities.length === 0 ? apiFetch(withServiceQuery('/users/cities')) : Promise.resolve(null),
        offers.length === 0 ? apiFetch(withServiceQuery('/users/offers')) : Promise.resolve(null),
        cachedCoords
          ? Promise.resolve(cachedCoords)
          : resolveStoredOrCurrentLocation({ forceRefresh: refreshLocation }),
      ]);

      let nextCities = cities;
      let nextOffers = offers;
      if (citiesRes) {
        const json = citiesRes.ok ? await citiesRes.json() : { data: [] };
        nextCities = extractList(json).filter(isAppServiceItem);
        setCities(nextCities);
      }
      if (offersRes) {
        const json = offersRes.ok ? await offersRes.json() : { data: [] };
        nextOffers = extractList(json).filter(isAppServiceItem);
        setOffers(nextOffers);
      }

      setLocationCoords(coords);
      if (coords) {
        setSelectedCity(resolveCityFromCoords(nextCities, coords.latitude, coords.longitude));
      } else {
        setSelectedCity(null);
      }
      setPricingReady(true);
    } catch {
      setSelectedCity(null);
      setLocationCoords(null);
      setPricingReady(true);
    } finally {
      setPricingLoading(false);
      setRefreshingLocation(false);
    }
  }, [cities, offers, hasItems]);

  const handleRefreshLocation = useCallback(async () => {
    await loadPricingContext({ refreshLocation: true });
  }, [loadPricingContext]);

  useEffect(() => { loadPricingContext(); }, [loadPricingContext]);

  const getWhatsAppLabels = useCallback(() => ({
    orderTitle: t('cart_wa_order_title'),
    consultTitle: t('cart_wa_consult_title'),
    name: t('cart_wa_name'),
    phone: t('cart_wa_phone'),
    city: t('cart_wa_city'),
    location: t('cart_wa_location'),
    map: t('cart_wa_map'),
    items: t('cart_wa_items'),
    note: t('cart_wa_note'),
    subtotal: t('cart_wa_subtotal'),
    delivery: t('cart_wa_delivery'),
    freeDelivery: t('cart_wa_free_delivery'),
    grandTotal: t('cart_wa_grand_total'),
  }), [t]);

  const buildWhatsAppPayload = useCallback(async ({ type, city, coords, orderTotals }) => {
    const user = await getStoredUser();
    const messageTotals = orderTotals ?? totals;
    return buildCartWhatsAppMessage({
      type,
      items,
      totals: {
        productsSubtotal: formatPrice(messageTotals.productsSubtotal ?? subtotal),
        deliveryFee: formatPrice(messageTotals.deliveryFee ?? 0),
        hasFreeDelivery: Boolean(messageTotals.hasFreeDelivery),
        grandTotal: formatPrice(messageTotals.grandTotal ?? subtotal),
      },
      city: city ?? selectedCity,
      coords,
      user,
      currency,
      labels: getWhatsAppLabels(),
    });
  }, [items, totals, subtotal, selectedCity, currency, getWhatsAppLabels]);

  const handlePlaceOrder = async () => {
    if (pricingLoading || !pricingReady) return;
    const loggedIn = await isUserLoggedIn();
    if (!loggedIn) { navigate('/register'); return; }

    const coords = getCachedLocation() ?? (await resolveStoredOrCurrentLocation());
    if (!coords) {
      showMessage(t('login_error'), t('login_location_required'));
      return;
    }

    const city = resolveCityFromCoords(cities, coords.latitude, coords.longitude) ?? selectedCity;
    if (!city?.id) {
      showMessage(t('cart_out_of_zone_title'), t('cart_out_of_zone_message'), 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/users/orders', {
        method: 'POST',
        body: JSON.stringify({
          service_id: APP_SERVICE_ID,
          location: coords.address,
          latitude: coords.latitude,
          longitude: coords.longitude,
          delivery_type: 'delivery',
          payment_method: 'cash',
          note: '',
          city_id: Number(city.id),
          items: items.map((entry) => ({
            product_id: Number(entry.product.id),
            quantity: entry.quantity,
            description: (entry.note ?? '').trim(),
          })),
        }),
      });
      const orderResponse = await readJsonSafe(response);
      if (response.status === 401) { navigate('/register'); return; }
      if (!response.ok) {
        showMessage(t('login_error'), orderResponse.message ?? t('cart_order_failed'));
        return;
      }

      const orderTotals = calculateDeliveryTotals({ subtotal, city, offers });
      const waMessage = await buildWhatsAppPayload({
        type: 'order',
        city,
        coords,
        orderTotals,
      });
      openWhatsApp(waMessage);

      dispatch(clearCart());
      showMessage(t('cart_order_success'), t('cart_order_success_message'), 'success');
    } catch {
      showMessage(t('login_error'), t('cart_order_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConsultWhatsApp = async () => {
    if (!hasItems || submitting) return;

    const coords = getCachedLocation()
      ?? locationCoords
      ?? (await resolveStoredOrCurrentLocation());
    const city = coords
      ? (resolveCityFromCoords(cities, coords.latitude, coords.longitude) ?? selectedCity)
      : selectedCity;

    const waMessage = await buildWhatsAppPayload({
      type: 'consult',
      city,
      coords,
    });
    openWhatsApp(waMessage);
    showMessage(t('cart_consult_opened'), t('cart_consult_opened_message'), 'success');
  };

  const canPlaceOrder = hasItems && pricingReady && !pricingLoading && !submitting;
  const canConsult = hasItems && !submitting;

  const offerPercentLabel =
    totals.offerDiscountPercent > 0
      ? Number.isInteger(totals.offerDiscountPercent)
        ? String(totals.offerDiscountPercent)
        : String(totals.offerDiscountPercent)
      : '';

  const summaryCard = (
    <Paper
      {...pagePaperSx}
      sx={{
        ...pagePaperSx.sx,
        p: { xs: 2.5, md: 3 },
        position: { md: 'sticky' },
        top: { md: 88 },
      }}
    >
      <LocationZoneSection
        t={t}
        loading={pricingLoading}
        ready={pricingReady}
        selectedCity={selectedCity}
        hasCoords={Boolean(locationCoords)}
        bestOfferTitle={totals.bestOffer?.title}
        onRefreshLocation={handleRefreshLocation}
        refreshingLocation={refreshingLocation}
      />

      <Typography
        variant="subtitle2"
        fontWeight={800}
        sx={{ color: 'primary.main', mb: 1.5, letterSpacing: 0.3 }}
      >
        {t('cart_grand_total')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, fontWeight: 600 }}>
        {t('orders_items_count', { count: itemCount })}
      </Typography>

      <Box
        sx={{
          p: 1.75,
          borderRadius: 3,
          bgcolor: alpha('#2a938f', 0.04),
          border: 1,
          borderColor: alpha('#2a938f', 0.1),
          mb: 1,
        }}
      >
        <OrderSummaryLine label={t('cart_subtotal')} value={`${formatPrice(subtotal)} ${currency}`} />
        {discountTotal > 0 && (
          <OrderSummaryLine label={t('cart_discount')} value={`-${formatPrice(discountTotal)} ${currency}`} success />
        )}
        {totals.offerDiscountAmount > 0 && (
          <OrderSummaryLine
            label={`${t('cart_offer_discount')}${offerPercentLabel ? ` (${offerPercentLabel}%)` : ''}`}
            value={`-${formatPrice(totals.offerDiscountAmount)} ${currency}`}
            success
          />
        )}
        <OrderSummaryLine
          label={t('cart_delivery_fee')}
          value={
            totals.hasFreeDelivery
              ? t('cart_delivery_free')
              : selectedCity
                ? `${formatPrice(totals.deliveryFee)} ${currency}`
                : '—'
          }
          success={totals.hasFreeDelivery}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 3,
          p: 2,
          borderRadius: 3,
          bgcolor: alpha('#2a938f', 0.07),
          border: 1,
          borderColor: alpha('#2a938f', 0.14),
        }}
      >
        <Typography variant="subtitle1" fontWeight={800}>{t('cart_grand_total')}</Typography>
        <Typography variant="h5" fontWeight={800} color="primary.main">
          {formatPrice(totals.grandTotal)} {currency}
        </Typography>
      </Box>

      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!canPlaceOrder}
        onClick={handlePlaceOrder}
        startIcon={
          submitting || pricingLoading
            ? <CircularProgress size={20} color="inherit" />
            : <LockOutlinedIcon />
        }
        sx={{ ...primaryButtonSx, mb: 1.5 }}
      >
        {t('cart_place_order')}
      </Button>

      <Button
        variant="outlined"
        size="large"
        fullWidth
        disabled={!canConsult}
        onClick={handleConsultWhatsApp}
        sx={{
          py: 1.5,
          borderRadius: 2.5,
          fontSize: '1rem',
          fontWeight: 800,
          mb: 1.5,
          textTransform: 'none',
          borderColor: '#25D366',
          color: '#128C7E',
          '&:hover': {
            borderColor: '#128C7E',
            bgcolor: alpha('#25D366', 0.08),
          },
        }}
      >
        {t('cart_consult_whatsapp')}
      </Button>

      <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ fontWeight: 600 }}>
        {t('cart_login_required')}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '70vh', pb: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: { xs: 2.5, md: 3 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              mb: 1.5,
              '& .MuiBreadcrumbs-li': { fontSize: '0.9rem' },
              '& .MuiLink-root:hover': { color: 'primary.main' },
            }}
          >
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Typography color="text.primary" fontWeight={700}>{t('cart_title')}</Typography>
          </Breadcrumbs>
          <PageSectionTitle
            title={t('cart_title')}
            subtitle={hasItems ? `${itemCount} ${t('products').toLowerCase()}` : undefined}
          />
        </Container>
      </Box>

      <Container maxWidth="lg">
        {!hasItems ? (
          <Paper
            {...pagePaperSx}
            sx={{
              ...pagePaperSx.sx,
              py: { xs: 8, md: 10 },
              px: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                bgcolor: alpha('#2a938f', 0.08),
                border: 1,
                borderColor: alpha('#2a938f', 0.14),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 8px 24px rgba(42,147,143,0.12)',
              }}
            >
              <ShoppingCartOutlinedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={800} color="primary.main" gutterBottom>
              {t('cart_empty')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              {t('search_hint')}
            </Typography>
            <Button component={RouterLink} to="/" variant="contained" size="large" sx={{ px: 4, ...primaryButtonSx }}>
              {t('categories')}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              {isDesktop ? (
                <TableContainer
                  component={Paper}
                  {...pagePaperSx}
                  sx={{ ...pagePaperSx.sx, overflow: 'hidden' }}
                >
                  <Table>
                    <TableHead
                      sx={{
                        bgcolor: alpha('#2a938f', 0.06),
                        '& .MuiTableCell-root': {
                          fontWeight: 800,
                          color: 'primary.main',
                          borderBottomColor: alpha('#2a938f', 0.12),
                        },
                      }}
                    >
                      <TableRow>
                        <TableCell>{t('products')}</TableCell>
                        <TableCell align="center">{t('quantity')}</TableCell>
                        <TableCell align="left">{t('cart_line_total')}</TableCell>
                        <TableCell align="center" width={56} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((entry) => {
                        const itemTotal = lineTotal(entry.product.price, entry.quantity);
                        return (
                          <TableRow
                            key={entry.product.id}
                            hover
                            sx={{
                              '&:last-child td': { borderBottom: 0 },
                              '&:hover': { bgcolor: alpha('#2a938f', 0.03) },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                  sx={{
                                    width: 76,
                                    height: 76,
                                    borderRadius: 3,
                                    p: 1,
                                    border: 1,
                                    borderColor: 'divider',
                                    bgcolor: alpha('#2a938f', 0.05),
                                    background:
                                      'linear-gradient(180deg, rgba(58,181,176,0.1) 0%, rgba(42,147,143,0.04) 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={entry.product.image}
                                    alt={entry.product.name}
                                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                  />
                                </Box>
                                <Box>
                                  <Typography fontWeight={800}>{entry.product.name}</Typography>
                                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                    {formatProductPrice(entry.product)} {currency}
                                  </Typography>
                                  {entry.note && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {entry.note}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <QuantityStepper
                                quantity={entry.quantity}
                                onDecrease={() => dispatch(setItemQuantity({ productId: entry.product.id, quantity: entry.quantity - 1 }))}
                                onIncrease={() => dispatch(setItemQuantity({ productId: entry.product.id, quantity: entry.quantity + 1 }))}
                              />
                            </TableCell>
                            <TableCell align="left">
                              <Typography fontWeight={800} color="primary.main" sx={{ fontSize: '1.02rem' }}>
                                {formatPrice(itemTotal)} {currency}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => dispatch(removeFromCart(entry.product.id))}
                                sx={{
                                  border: 1,
                                  borderColor: alpha('#d32f2f', 0.2),
                                  bgcolor: alpha('#d32f2f', 0.04),
                                }}
                              >
                                <DeleteOutlinedIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Stack spacing={2}>
                  {items.map((entry) => (
                    <CartItemMobile
                      key={entry.product.id}
                      entry={entry}
                      currency={currency}
                      onDecrease={() => dispatch(setItemQuantity({ productId: entry.product.id, quantity: entry.quantity - 1 }))}
                      onIncrease={() => dispatch(setItemQuantity({ productId: entry.product.id, quantity: entry.quantity + 1 }))}
                      onRemove={() => dispatch(removeFromCart(entry.product.id))}
                    />
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              {summaryCard}
            </Grid>
          </Grid>
        )}
      </Container>

      <MessageModal
        visible={messageModal.visible}
        onClose={() => setMessageModal((p) => ({ ...p, visible: false }))}
        title={messageModal.title}
        message={messageModal.message}
        variant={messageModal.variant}
      />
    </Box>
  );
}
